import { useId, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { SUPPORT_TEXT_MAX_LENGTH, type SupportRequesterStatus } from '@onr/schema'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import { ChevronLeftIcon, PaperclipIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { CharacterCounter } from '@/components/atoms/CharacterCounter'
import { InlineError } from '@/components/atoms/InlineError'
import { TextArea } from '@/components/atoms/TextArea/TextArea.tsx'
import { SupportStatusPill } from '@/components/molecules/SupportStatusPill'
import { SupportThreadItem, type SupportThreadItemProps } from '@/components/molecules/SupportThreadItem'
import { SUPPORT_COUNTER_THRESHOLD } from '@/components/organisms/SupportRequestForm'

export type SupportConversationItem = SupportThreadItemProps & { readonly id: string }

export interface SupportConversationProps {
  readonly meta: string
  readonly title: string
  readonly requesterStatus: SupportRequesterStatus
  readonly assigneeLine: string
  readonly contextLine: string
  readonly items: ReadonlyArray<SupportConversationItem>
  readonly replying: boolean
  readonly replyError: boolean
  readonly settling: boolean
  readonly settleError: boolean
  readonly onBack: () => void
  readonly onReply: (text: string) => Promise<boolean>
  readonly onSettle: () => void
}

export const SupportConversation = ({
  meta,
  title,
  requesterStatus,
  assigneeLine,
  contextLine,
  items,
  replying,
  replyError,
  settling,
  settleError,
  onBack,
  onReply,
  onSettle,
}: SupportConversationProps) => {
  const intl = useIntl()
  const titleId = useId()
  const replyId = useId()
  const [reply, setReply] = useState('')
  const threadRef = useRef<HTMLDivElement>(null)
  const settled = requesterStatus === 'settled'

  const submitReply = async (text: string) => {
    if (await onReply(text)) {
      setReply('')
    }
  }

  useLayoutEffect(() => {
    const thread = threadRef.current
    if (thread && items.length > 0) {
      thread.scrollTop = thread.scrollHeight
    }
  }, [items.length])

  return (
    <section aria-labelledby={titleId} className="flex h-full flex-col">
      <header className="flex flex-col gap-2.5 border-b border-gray-200 px-6 py-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex cursor-pointer items-center gap-1 self-start text-sm text-blue-700 hover:text-blue-600">
          <ChevronLeftIcon aria-hidden className="size-3.5" />
          {intl.formatMessage({ id: 'soutien.conversation.back' })}
        </button>
        <span className="text-sm text-gray-600">{meta}</span>
        <h2 id={titleId} className="font-heading text-lg leading-[1.3] font-semibold text-gray-900">
          {title}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <SupportStatusPill status={requesterStatus} />
          <span className="text-sm text-gray-600">{assigneeLine}</span>
        </div>
      </header>

      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-2.5 text-sm text-gray-600">
        <PaperclipIcon aria-hidden className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 leading-[1.35]">{contextLine}</span>
      </div>

      <div ref={threadRef} data-testid="support-thread" className="flex flex-1 flex-col overflow-y-auto">
        {items.map(({ id, ...item }, index) => (
          <SupportThreadItem key={id} {...item} className={index === items.length - 1 ? 'border-b-0' : undefined} />
        ))}

        <form
          className="flex flex-col gap-2.5 border-t border-gray-200 px-6 pt-8 pb-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (reply.trim() === '' || replying) {
              return
            }
            handleNotAwaitedPromise(submitReply(reply))
          }}>
          <label htmlFor={replyId} className="font-bold text-gray-800">
            {intl.formatMessage({ id: 'soutien.conversation.reply.label' })}
          </label>
          <TextArea
            id={replyId}
            rows={3}
            value={reply}
            maxLength={SUPPORT_TEXT_MAX_LENGTH}
            aria-describedby={settled ? `${replyId}-help` : undefined}
            placeholder={intl.formatMessage({ id: 'soutien.conversation.reply.placeholder' })}
            className="resize-y border-gray-300 py-2 text-base leading-[1.45] text-gray-900 placeholder:text-gray-400"
            onChange={(event) => setReply(event.target.value)}
          />
          <CharacterCounter length={reply.length} max={SUPPORT_TEXT_MAX_LENGTH} showFrom={SUPPORT_COUNTER_THRESHOLD} />
          {replyError && <InlineError message={intl.formatMessage({ id: 'soutien.conversation.reply.error' })} />}
          {settleError && <InlineError message={intl.formatMessage({ id: 'soutien.conversation.settle.error' })} />}
          <div className="flex flex-wrap items-center justify-between gap-3">
            {settled ? (
              <span id={`${replyId}-help`} className="text-sm text-gray-600">
                {intl.formatMessage({ id: 'soutien.conversation.reply.reopens' })}
              </span>
            ) : (
              <button
                type="button"
                disabled={settling}
                onClick={onSettle}
                className="cursor-pointer py-2 text-base text-blue-700 hover:text-blue-600 disabled:opacity-50">
                {intl.formatMessage({ id: 'soutien.conversation.settle' })}
              </button>
            )}
            <Button
              type="submit"
              variant="primary"
              className="h-10 px-6"
              loading={replying}
              disabled={reply.trim() === ''}>
              {intl.formatMessage({ id: 'soutien.conversation.reply.submit' })}
            </Button>
          </div>
        </form>
      </div>
    </section>
  )
}
